import { useEffect } from "react";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  useGetPageFromPath,
  useVisibleGroups,
  useVisibleProcesses,
} from "utils/customHooks/genericHooks";
import { useAtom } from "jotai";
import { authExpiredAtom } from "./AuthenticationFailedIndicator";

/** for internal urls (fledge, localhost) or raven emails only,
 *
 * whenever the url changes,
 * add e.g. "| RAVENG Group Tiles" to the document.title (appears on the tab
 * & in your browser history) to show what page we're on
 */
export default function SyncDocumentTitleToUrlContents() {
  // whenever the url changes,
  // update the document.title (appears on the tab)
  const ckey = useCustomerKey();
  const currentPageName = useCurrentPageName();
  const [authExpired] = useAtom(authExpiredAtom);

  useEffect(() => {
    const suffix = currentPageName
      ? ` | ${ckey.toUpperCase()} ${currentPageName}`
      : "";

    const authExpiredIndicator = authExpired ? " | Session Expired" : "";
    document.title = `Raven Flight${suffix}${authExpiredIndicator}`;
  }, [currentPageName, ckey, authExpired]);

  return null;
}

// tslint:disable-next-line
export function useCurrentPageName() {
  const {
    isProcessTilesPage,
    isGroupTilesPage,
    isTimelinePage,
    isHubletPage,
    isGridLayoutPage,
    isHelpPage,
    isSettingsPage,
    isCustomerTilesPage,
    isTrendsPage,
    isUnauthenticatedPage,
    isPowerBiPage,
    isReportsPage,
  } = useGetPageFromPath();
  const visibleGroups = useVisibleGroups();
  const visibleProcesses = useVisibleProcesses();
  const visibleGroup = visibleGroups.length === 1 ? visibleGroups[0] : null;
  const visibleProcess =
    visibleProcesses.length === 1 ? visibleProcesses[0] : null;

  // if we have group or process, add a space after their names
  const groupName = visibleGroup?.shortName
    ? `${visibleGroup?.shortName} `
    : "";
  const processName = visibleProcess?.name ? `${visibleProcess?.name} ` : "";

  switch (true) {
    case isCustomerTilesPage:
      return "";
    case isProcessTilesPage:
      return `${groupName}Process Tiles`;
    case isGroupTilesPage:
      return `Group Tiles`;
    case isTimelinePage:
      return `${groupName}Timeline`;
    case isHubletPage:
      return `${processName}Hublet`;
    case isGridLayoutPage:
      return `${processName}Grid`;
    case isTrendsPage:
      return "Trends";
    case isHelpPage:
      return "Help";
    case isSettingsPage:
      return "Settings";
    case isPowerBiPage:
      return "Sight";
    case isReportsPage:
      return "Reports";
    case isUnauthenticatedPage:
      return "Unauthenticated";
    default:
      return "Unknown";
  }
}
